import { useEffect, useState } from "react";
import { BOOK_DEMO_EVENTS, pushToDataLayer } from "@/services/gtm";

const EMBED_SCRIPT_SRC =
  "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";

/** Only trust postMessage payloads that actually came from HubSpot. */
const isHubSpotOrigin = (origin: string) => {
  try {
    return new URL(origin).hostname.endsWith(".hubspot.com");
  } catch {
    return false;
  }
};

const withEmbedParam = (url: string) => {
  const separator = url.includes("?") ? "&" : "?";
  return url.includes("embed=true") ? url : `${url}${separator}embed=true`;
};

interface HubSpotMeetingsProps {
  /** Scheduler URL, e.g. https://meetings-eu1.hubspot.com/<user>/<slug> */
  url: string;
  className?: string;
}

/**
 * Renders the HubSpot Meeting Scheduler inline and reports the funnel to GTM.
 *
 * Booking happens on-page rather than off-site, which is what lets GTM observe
 * the whole flow: scheduler load, and the confirmed booking as a conversion.
 */
export default function HubSpotMeetings({ url, className = "" }: HubSpotMeetingsProps) {
  const [hasBooked, setHasBooked] = useState(false);

  // The embed script only scans the DOM for `.meetings-iframe-container` at the
  // moment it runs, so on client-side navigation it has to be re-appended
  // rather than reused — otherwise the container stays empty.
  useEffect(() => {
    const script = document.createElement("script");
    script.src = EMBED_SCRIPT_SRC;
    script.async = true;
    script.onload = () =>
      pushToDataLayer({
        event: BOOK_DEMO_EVENTS.schedulerLoaded,
        scheduler_url: url,
      });

    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [url]);

  // HubSpot notifies the parent window once a meeting is confirmed.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isHubSpotOrigin(event.origin)) return;
      if (event.data?.meetingBookSucceeded !== true) return;

      const booking = event.data?.meetingsPayload?.bookingResponse;

      pushToDataLayer({
        event: BOOK_DEMO_EVENTS.meetingBooked,
        scheduler_url: url,
        meeting_duration: booking?.event?.duration,
        meeting_organizer: booking?.event?.organizer,
      });

      // Mirror it into GA4 directly, alongside the existing gtag events.
      window.gtag?.("event", "generate_lead", {
        event_category: "book_demo",
        event_label: "Meeting booked",
        value: 1,
      });

      setHasBooked(true);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [url]);

  return (
    // The iframe is injected by HubSpot's script, so it is styled from here via
    // child selectors. `embed=true` makes HubSpot's own body transparent, and
    // keeping the iframe transparent too lets the page background show through.
    <div
      className={`meetings-iframe-container min-h-[760px] w-full [&>iframe]:!bg-transparent [&>iframe]:w-full [&>iframe]:border-0 ${className}`}
      data-src={withEmbedParam(url)}
      data-booked={hasBooked ? "true" : undefined}
    />
  );
}
